import { supabase } from '../../lib/supabase';
import { getGameNameById } from '../../constants/deckRules';
import { TournamentMetaDeck, DEFAULT_META_DECKS } from './tournamentReportsService';

export interface MetaDeckStatItem {
  id: number | string;
  game_id: number;
  game_name?: string;
  name: string;
  slug?: string;
  colors?: string[] | null;
  image_url?: string | null;
  code?: string | null;
  display_name?: string | null;
  representative_card_id?: string | null;
  game_format_id?: number | null;
  format_name?: string | null;
  created_at?: string;
  updated_at?: string;
  rounds_count: number;
  reports_count: number;
  meta_share: number; // Porcentagem do meta sobre o total de rounds (0-100)
  report_share: number; // Porcentagem de torneios que enfrentaram esse deck (0-100)
  tier: 'Tier 1' | 'Tier 2' | 'Tier 3' | 'Rogue';
  wins_against?: number; // Vezes que jogadores venceram contra este meta deck
  losses_against?: number; // Vezes que jogadores perderam contra este meta deck
  draws_against?: number; // Empates
  win_rate_against?: number; // % de vitórias dos jogadores contra esse deck
}

export interface MetaMatchupData {
  round_number: number;
  result: 'win' | 'loss' | 'draw';
  player_deck_name?: string;
  opponent_name?: string;
  tournament_name?: string;
  played_at?: string;
  event_category_name?: string;
}

/**
 * Busca estatísticas de meta decks a partir da view public.v_tournament_meta_decks_stats
 */
export const getTournamentMetaDecksStats = async (
  gameId?: number | string,
  formatId?: number | string
): Promise<MetaDeckStatItem[]> => {
  const numericGameId = gameId && gameId !== 'All' ? Number(gameId) : null;
  const numericFormatId = formatId && formatId !== 'All' ? Number(formatId) : null;

  // Um jogo selecionado é um requisito estrito para a análise de metagame
  if (!numericGameId) {
    return [];
  }

  let rawData: any[] = [];
  let fetchedFromView = false;

  try {
    let query = supabase
      .from('v_tournament_meta_decks_stats')
      .select('*')
      .eq('game_id', numericGameId);

    if (numericFormatId) {
      query = query.eq('game_format_id', numericFormatId);
    }

    // Ordenar pelos decks com maior presença
    query = query.order('rounds_count', { ascending: false });

    const { data, error } = await query;

    if (!error && Array.isArray(data) && data.length > 0) {
      rawData = data;
      fetchedFromView = true;
    } else if (error) {
      console.warn('Could not fetch from v_tournament_meta_decks_stats view:', error.message);
    }
  } catch (err) {
    console.warn('Exception querying v_tournament_meta_decks_stats:', err);
  }

  // Fallback se a view ainda não retornou dados ou retornou vazio
  if (!fetchedFromView || rawData.length === 0) {
    try {
      // Buscar decks da tabela base tournament_meta_decks
      let metaQuery = supabase
        .from('tournament_meta_decks')
        .select('*');

      if (numericGameId) {
        metaQuery = metaQuery.eq('game_id', numericGameId);
      }
      if (numericFormatId) {
        metaQuery = metaQuery.eq('game_format_id', numericFormatId);
      }

      const { data: metaDecksData } = await metaQuery;

      const baseList = (metaDecksData && metaDecksData.length > 0)
        ? metaDecksData
        : (numericGameId ? DEFAULT_META_DECKS.filter(d => Number(d.game_id) === numericGameId) : DEFAULT_META_DECKS);

      // Buscar rodadas para contagem agregada
      const { data: roundsData } = await supabase
        .from('user_tournament_report_rounds')
        .select('id, opponent_meta_deck_id, report_id, result');

      const roundsByMeta: Record<string, { rounds: number; reports: Set<string>; wins: number; losses: number; draws: number }> = {};

      if (Array.isArray(roundsData)) {
        roundsData.forEach(r => {
          if (r.opponent_meta_deck_id) {
            const mId = String(r.opponent_meta_deck_id);
            if (!roundsByMeta[mId]) {
              roundsByMeta[mId] = { rounds: 0, reports: new Set(), wins: 0, losses: 0, draws: 0 };
            }
            roundsByMeta[mId].rounds += 1;
            if (r.report_id) roundsByMeta[mId].reports.add(String(r.report_id));
            if (r.result === 'win') roundsByMeta[mId].wins += 1;
            if (r.result === 'loss') roundsByMeta[mId].losses += 1;
            if (r.result === 'draw') roundsByMeta[mId].draws += 1;
          }
        });
      }

      rawData = baseList.map(md => {
        const stats = roundsByMeta[String(md.id)] || { rounds: 0, reports: new Set(), wins: 0, losses: 0, draws: 0 };
        return {
          id: md.id,
          game_id: Number(md.game_id),
          name: md.name,
          slug: md.slug,
          colors: md.colors,
          image_url: md.image_url,
          code: md.code || null,
          display_name: md.display_name || md.name,
          representative_card_id: md.representative_card_id || null,
          game_format_id: (md as any).game_format_id || null,
          created_at: (md as any).created_at || new Date().toISOString(),
          updated_at: (md as any).updated_at || new Date().toISOString(),
          rounds_count: stats.rounds,
          reports_count: stats.reports.size,
          wins_against: stats.wins,
          losses_against: stats.losses,
          draws_against: stats.draws
        };
      });
    } catch (err) {
      console.warn('Fallback error for meta decks stats:', err);
    }
  }

  // Buscar formatos e resultados das rodadas para enriquecer estatísticas de confronto
  let roundResultsMap: Record<string, { wins: number; losses: number; draws: number }> = {};
  try {
    const { data: roundsDetails } = await supabase
      .from('user_tournament_report_rounds')
      .select('opponent_meta_deck_id, result');

    if (Array.isArray(roundsDetails)) {
      roundsDetails.forEach(r => {
        if (r.opponent_meta_deck_id) {
          const mId = String(r.opponent_meta_deck_id);
          if (!roundResultsMap[mId]) {
            roundResultsMap[mId] = { wins: 0, losses: 0, draws: 0 };
          }
          if (r.result === 'win') roundResultsMap[mId].wins += 1;
          else if (r.result === 'loss') roundResultsMap[mId].losses += 1;
          else if (r.result === 'draw') roundResultsMap[mId].draws += 1;
        }
      });
    }
  } catch (err) {
    console.warn('Could not fetch round details for winrate:', err);
  }

  // Calcular total de rodadas e total de reports no escopo
  const totalRounds = rawData.reduce((acc, cur) => acc + (Number(cur.rounds_count) || 0), 0);
  const totalReports = rawData.reduce((acc, cur) => acc + (Number(cur.reports_count) || 0), 0);

  // Mapear dados com porcentagens, Tier e winrate
  const processedList: MetaDeckStatItem[] = rawData.map(item => {
    const rounds = Number(item.rounds_count) || 0;
    const reports = Number(item.reports_count) || 0;
    const metaShare = totalRounds > 0 ? (rounds / totalRounds) * 100 : 0;
    const reportShare = totalReports > 0 ? (reports / totalReports) * 100 : 0;

    const res = roundResultsMap[String(item.id)] || {
      wins: item.wins_against || 0,
      losses: item.losses_against || 0,
      draws: item.draws_against || 0
    };

    const totalDecided = res.wins + res.losses;
    const winRateAgainst = totalDecided > 0 ? Math.round((res.wins / totalDecided) * 100) : 50;

    // Definição de Tiers baseada na presença no meta
    let tier: 'Tier 1' | 'Tier 2' | 'Tier 3' | 'Rogue' = 'Rogue';
    if (metaShare >= 15 || (totalRounds === 0 && (item.id === 101 || item.id === 102 || item.id === 201 || item.id === 301))) {
      tier = 'Tier 1';
    } else if (metaShare >= 7.5) {
      tier = 'Tier 2';
    } else if (metaShare >= 2.5 || rounds > 0) {
      tier = 'Tier 3';
    }

    return {
      id: item.id,
      game_id: Number(item.game_id),
      game_name: getGameNameById(Number(item.game_id)),
      name: item.name,
      slug: item.slug,
      colors: Array.isArray(item.colors) ? item.colors : (typeof item.colors === 'string' ? JSON.parse(item.colors) : []),
      image_url: item.image_url,
      code: item.code,
      display_name: item.display_name || item.name,
      representative_card_id: item.representative_card_id,
      game_format_id: item.game_format_id ? Number(item.game_format_id) : null,
      created_at: item.created_at,
      updated_at: item.updated_at,
      rounds_count: rounds,
      reports_count: reports,
      meta_share: Number(metaShare.toFixed(1)),
      report_share: Number(reportShare.toFixed(1)),
      tier,
      wins_against: res.wins,
      losses_against: res.losses,
      draws_against: res.draws,
      win_rate_against: winRateAgainst
    };
  });

  // Ordenar por presença no meta (rounds_count decrescente)
  processedList.sort((a, b) => b.rounds_count - a.rounds_count || b.reports_count - a.reports_count || a.name.localeCompare(b.name));

  // Se todos tiverem 0 rounds (banco inicial), atribuir Tiers de demonstração bem definidos para visualização
  if (totalRounds === 0) {
    return processedList.map((item, index) => {
      let tier: 'Tier 1' | 'Tier 2' | 'Tier 3' | 'Rogue' = 'Rogue';
      if (index < 2) tier = 'Tier 1';
      else if (index < 5) tier = 'Tier 2';
      else if (index < 8) tier = 'Tier 3';

      return {
        ...item,
        tier
      };
    });
  }

  return processedList;
};

/**
 * Busca histórico recente de confrontos contra um meta deck específico
 */
export const getMetaDeckMatchups = async (metaDeckId: number | string): Promise<MetaMatchupData[]> => {
  try {
    const { data, error } = await supabase
      .from('user_tournament_report_rounds')
      .select(`
        round_number,
        result,
        opponent_name,
        user_tournament_reports:report_id(
          name,
          played_at,
          deck_name,
          tournament_event_categories:event_category_id(name)
        )
      `)
      .eq('opponent_meta_deck_id', Number(metaDeckId))
      .order('created_at', { ascending: false })
      .limit(20);

    if (!error && Array.isArray(data)) {
      return data.map((r: any) => {
        const report = r.user_tournament_reports || {};
        return {
          round_number: r.round_number,
          result: r.result,
          opponent_name: r.opponent_name || 'Oponente',
          player_deck_name: report.deck_name || 'Deck do Jogador',
          tournament_name: report.name || 'Torneio',
          played_at: report.played_at || '',
          event_category_name: report.tournament_event_categories?.name || 'Local'
        };
      });
    }
  } catch (err) {
    console.warn('Error fetching meta deck matchups:', err);
  }

  return [];
};
